import { useState, type ClipboardEvent } from 'react';
import { CornerDownLeft, UserPlus } from 'lucide-react';
import { parseQuickGuest, type QuickGuest } from '@peles/shared';
import { useCreateEventGuest } from '../../../hooks/useEventGuests';
import { extractApiErrorMessage } from '../../../utils/apiError';

function toInput(guest: QuickGuest) {
  return {
    firstName: guest.firstName,
    ...(guest.lastName ? { lastName: guest.lastName } : {}),
    ...(guest.email ? { email: guest.email } : {}),
    ...(guest.phone ? { phone: guest.phone } : {}),
    maleCount: guest.partySize ?? 1,
  };
}

/**
 * Add guests without opening a form: type a name and press Enter, then the
 * next. A line may carry more — "David Levi, david@x.com, 2" — and pasting
 * several lines adds each one.
 */
export function GuestQuickAdd({ eventId }: { eventId: string }) {
  const create = useCreateEventGuest(eventId);
  const [text, setText] = useState('');
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  const addAll = async (lines: string[]) => {
    const guests = lines.map(parseQuickGuest).filter((g): g is QuickGuest => !!g);
    if (guests.length === 0) {
      setStatus({ tone: 'error', message: 'Type a name first.' });
      return false;
    }
    let added = 0;
    for (const guest of guests) {
      try {
        await create.mutateAsync(toInput(guest));
        added += 1;
      } catch (err) {
        setStatus({
          tone: 'error',
          message: `${added ? `Added ${added}. ` : ''}${guest.firstName}: ${extractApiErrorMessage(
            err,
            'could not be added',
          )}`,
        });
        return false;
      }
    }
    setStatus({
      tone: 'ok',
      message: added === 1 ? `Added ${guests[0].firstName}` : `Added ${added} guests`,
    });
    return true;
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    const lines = pasted.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return;
    e.preventDefault();
    void addAll(lines).then((ok) => ok && setText(''));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void addAll([text]).then((ok) => ok && setText(''));
      }}
      className="rounded-2xl border border-dashed border-gray-300 bg-white px-3 py-2"
      data-testid="guest-quick-add"
    >
      <div className="flex items-center gap-2">
        <UserPlus size={16} className="text-gray-400 flex-shrink-0" aria-hidden />
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setStatus(null);
          }}
          onPaste={onPaste}
          disabled={create.isPending}
          placeholder="Quick add: David Levi, david@x.com, 2"
          aria-label="Quick add a guest"
          className="flex-1 min-w-0 py-1.5 bg-transparent text-base sm:text-sm focus:outline-none"
        />
        <button
          type="submit"
          disabled={create.isPending || !text.trim()}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm text-primary-700 hover:bg-primary-50 disabled:opacity-40"
        >
          <CornerDownLeft size={14} aria-hidden />
          Add
        </button>
      </div>
      {status && (
        <p
          role="status"
          className={`mt-1 text-xs ${status.tone === 'ok' ? 'text-green-700' : 'text-red-700'}`}
        >
          {status.message}
        </p>
      )}
    </form>
  );
}
